import Box from "@mui/material/Box";
import React, { useContext, useEffect, useMemo, useState } from "react";
import RouteBreadcrumbs from "../shared/RouteBreadcrumbs";
import { CustomerDropdown } from "../CustomerDropdown";
import { Customer } from "../shared/interfaces/Customer";
import { CompanyDropdown } from "../CompanyDropdown";
import { MessageToast, ToastMode } from "../shared/MessageToast";
import { SiteGrid } from "./SiteGrid";
import { useParams } from "react-router-dom";
import { useCompaniesByCustomerId } from "../data/useCompaniesByCustomerId";
import { useSitesByCompanyId } from "../data/useSitesByCompanyId";
import { AuthContext } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Snackbar } from "../shared/Snackbar";
import { SiteCreate } from "./SiteCreate";
import { useAlternativeRegionNames } from "../data/Settings/useAlternativeRegionNames";
import { Stack } from "@mui/material";

export function SitesViewer() {
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");

  // Get the company name from the URL.
  const { companyName } = useParams<{ companyName?: string }>();

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | undefined>();
  const [selectedCompanyName, setSelectedCompanyName] = useState<string | undefined>(companyName);

  const altRegionNames = useAlternativeRegionNames(selectedCustomer?.id);

  const companies = useCompaniesByCustomerId(selectedCustomer?.id);

  const companyId = useMemo(() => {
    if (companies.data && selectedCompanyName) {
      return companies.data?.find((c) => c.name === selectedCompanyName)?.id;
    }
  }, [companies.data, selectedCompanyName]);

  const { refetch: refetchSites, ...sites } = useSitesByCompanyId(companyId);

  const [siteCreatePanelOpen, setSiteCreatePanelOpen] = useState(false);

  useEffect(() => {
    if (authContext.userIsAuthenticated) {
      refetchSites();
    }
  }, [authContext.userIsAuthenticated, refetchSites]);

  const errorToast = useMemo(() => {
    if (sites.error) {
      return {
        show: true,
        mode: ToastMode.Error,
        text: t("common.dataFetchError", {
          val: altRegionNames.data?.sitesAltName ?? "$t(settings.sites)",
        }),
      };
    } else {
      return { show: false, mode: ToastMode.Error, text: "" };
    }
  }, [t, sites.error, altRegionNames.data?.sitesAltName]);

  function handleCustomerSelect(customer?: Customer) {
    setSelectedCustomer(customer);
  }

  function handleCompanyChange(selectedCompanyName: string) {
    setSelectedCompanyName(selectedCompanyName);
  }

  function handleNewSiteButtonClick() {
    setSiteCreatePanelOpen(true);
  }

  function handleNewSitePanelClose() {
    setSiteCreatePanelOpen(false);
  }

  function handleSiteCreated(siteId: number, siteName: string) {
    refetchSites();
    setSnackbarText(
      t("common.dataSuccessfullyCreated", {
        entity: altRegionNames.data?.siteAltName ?? "$t(common.site)",
        id: siteName || siteId,
      }),
    );
    setShowSnackbar(true);
    setSiteCreatePanelOpen(false);
  }

  return (
    <React.Fragment>
      <Box marginBottom={2}>
        <RouteBreadcrumbs />
      </Box>

      <Stack direction="row">
        <CustomerDropdown
          selectedCustomer={selectedCustomer}
          onCustomerSelect={handleCustomerSelect}
          onSingleCustomerAvailable={(customer) => setSelectedCustomer(customer)}
          showLabel
        />
        <CompanyDropdown
          customerId={selectedCustomer?.id}
          selectedCompanyName={selectedCompanyName}
          onCompanyChange={handleCompanyChange}
          showLabel
        />
      </Stack>

      {errorToast.show ? (
        <div className="w-100 d-flex justify-content-center pt-2">
          <MessageToast text={errorToast.text} mode={errorToast.mode} show={errorToast.show}></MessageToast>
        </div>
      ) : (
        selectedCustomer &&
        selectedCompanyName && (
          <Box marginTop={2}>
            <SiteGrid
              customerSelected={selectedCustomer != null}
              customerId={selectedCustomer?.id}
              sitesData={sites.data}
              sitesDataLoading={sites.isLoading}
              sitesDataFetching={sites.isFetching}
              onDataRefreshClick={refetchSites}
              onNewSiteClick={handleNewSiteButtonClick}
              refreshSiteData={refetchSites}
            />

            {siteCreatePanelOpen && sites.data && (
              <SiteCreate
                open={siteCreatePanelOpen}
                onClose={handleNewSitePanelClose}
                customerId={selectedCustomer.id}
                companyId={companyId}
                onSiteCreated={handleSiteCreated}
                existingSites={sites.data}
                refreshSiteData={refetchSites}
              />
            )}
          </Box>
        )
      )}

      <Snackbar
        open={showSnackbar}
        onClose={() => {
          setShowSnackbar(false);
        }}
        message={snackbarText}
      />
    </React.Fragment>
  );
}
